import { supabase } from "./supabase";
import {
  parseAuthOutcome,
  recordAuthOutcome,
  type AuthOutcome,
} from "./authOutcome";

export type InternationalHost = "line" | "discord" | "teams" | "vk" | "max";
export type InternationalShareResult = "shared" | "copied" | "cancelled" | "failed";

type HostHint = {
  id?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
  language?: string;
};

const lineLiffId = import.meta.env.VITE_LINE_LIFF_ID as string | undefined;
const discordClientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;

let lineInit: Promise<void> | null = null;
let discordSdk: import("@discord/embedded-app-sdk").DiscordSDK | null = null;
let discordInit: Promise<void> | null = null;
let teamsInit: Promise<void> | null = null;
const hints: Partial<Record<InternationalHost, HostHint>> = {};

export function isInternationalHost(value: string): value is InternationalHost {
  return (
    value === "line" ||
    value === "discord" ||
    value === "teams" ||
    value === "vk" ||
    value === "max"
  );
}

export function getInternationalHostHint(host: InternationalHost): HostHint | null {
  return hints[host] ?? null;
}

export async function initializeInternationalHost(host: InternationalHost): Promise<void> {
  if (host === "line") {
    if (!lineLiffId) throw new Error("VITE_LINE_LIFF_ID is not configured");
    lineInit ??= import("@line/liff").then(async ({ default: liff }) => {
      await liff.init({ liffId: lineLiffId });
      const decoded = liff.getDecodedIDToken() as (Record<string, unknown> & { locale?: string }) | null;
      let profile: Awaited<ReturnType<typeof liff.getProfile>> | null = null;
      if (liff.isLoggedIn()) profile = await liff.getProfile().catch(() => null);
      hints.line = {
        id: profile?.userId,
        firstName: profile?.displayName,
        photoUrl: profile?.pictureUrl,
        language: typeof decoded?.locale === "string" ? decoded.locale : undefined,
      };
    });
    return await lineInit;
  }

  if (host === "discord") {
    if (!discordClientId) throw new Error("VITE_DISCORD_CLIENT_ID is not configured");
    if (!discordSdk) {
      const { DiscordSDK } = await import("@discord/embedded-app-sdk");
      discordSdk = new DiscordSDK(discordClientId);
    }
    discordInit ??= discordSdk.ready();
    return await discordInit;
  }

  if (host === "vk" || host === "max") {
    // Their bridges are brought up by the platform adapter (lib/platform.ts),
    // which also decides that this is a VK or MAX launch in the first place.
    return;
  }

  teamsInit ??= import("@microsoft/teams-js").then(async ({ app }) => {
    await app.initialize();
    const context = await app.getContext();
    hints.teams = {
      id: context.user?.id,
      username: context.user?.loginHint,
      language: context.app.locale,
    };
  });
  return await teamsInit;
}

async function getVerifiedProof(host: InternationalHost): Promise<string> {
  await initializeInternationalHost(host);
  if (host === "line") {
    const { default: liff } = await import("@line/liff");
    if (!liff.isLoggedIn()) {
      liff.login();
      throw new Error("line_login_redirect");
    }
    const token = liff.getIDToken();
    if (!token) throw new Error("LINE did not provide an ID token; enable the openid scope");
    return token;
  }
  if (host === "discord") {
    if (!discordSdk || !discordClientId) throw new Error("Discord SDK is not ready");
    const result = await discordSdk.commands.authorize({
      client_id: discordClientId,
      response_type: "code",
      prompt: "none",
      scope: ["identify"],
    });
    if (!result.code) throw new Error("Discord did not return an authorization code");
    return result.code;
  }
  if (host === "vk") {
    // VK signs the launch parameters it put in this URL; the app never holds
    // the key that signed them, so the query string itself is the proof.
    const params = window.location.search;
    if (!params.includes("sign=")) throw new Error("VK launch parameters are missing");
    return params;
  }
  if (host === "max") {
    // MAX passes Telegram-shaped signed initData through its bridge.
    const initData = (window as unknown as { WebApp?: { initData?: string } }).WebApp?.initData;
    if (!initData) throw new Error("MAX did not provide initData");
    return initData;
  }
  const { authentication } = await import("@microsoft/teams-js");
  return await authentication.getAuthToken();
}

export type HostAuthErrorCode =
  | "invalid_code"
  | "code_used"
  | "code_expired"
  | "too_many_attempts"
  | "identity_already_linked"
  | "provider_already_linked"
  | "invalid_provider_proof"
  | "provider_not_configured"
  | "internal"
  | "network";

export class HostAuthError extends Error {
  constructor(public readonly code: HostAuthErrorCode, message: string) {
    super(message);
    this.name = "HostAuthError";
  }
}

function normalizeErrorCode(value: unknown): HostAuthErrorCode {
  const known: HostAuthErrorCode[] = [
    "invalid_code",
    "code_used",
    "code_expired",
    "too_many_attempts",
    "identity_already_linked",
    "provider_already_linked",
    "invalid_provider_proof",
    "provider_not_configured",
    "internal",
  ];
  return known.includes(value as HostAuthErrorCode) ? (value as HostAuthErrorCode) : "internal";
}

export async function signInWithInternationalHost(
  host: InternationalHost,
  linkCode?: string,
): Promise<AuthOutcome> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!supabase || !supabaseUrl || !anonKey) throw new Error("Supabase is not configured");
  const proof = await getVerifiedProof(host);
  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/host-auth`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: anonKey },
      body: JSON.stringify({
        provider: host,
        proof,
        ...(linkCode ? { linkCode } : {}),
      }),
    });
  } catch {
    throw new HostAuthError("network", "Could not reach the sign-in service");
  }
  const body = await response.json().catch(() => ({})) as {
    token_hash?: string;
    discord_access_token?: string;
    is_new_user?: boolean;
    origin_surface?: unknown;
    error?: { code?: unknown; message?: unknown };
  };
  if (!response.ok || !body.token_hash) {
    throw new HostAuthError(
      normalizeErrorCode(body.error?.code),
      typeof body.error?.message === "string" ? body.error.message : "Host sign-in failed",
    );
  }
  const { error } = await supabase.auth.verifyOtp({ type: "email", token_hash: body.token_hash });
  if (error) throw error;
  const outcome = parseAuthOutcome(body);

  // Discord needs its own access token as well as the Supabase session. It is
  // used once inside the Activity SDK and is never persisted by Lexibit.
  if (host === "discord" && body.discord_access_token && discordSdk) {
    const authenticated = await discordSdk.commands.authenticate({
      access_token: body.discord_access_token,
    });
    hints.discord = {
      id: authenticated.user.id,
      username: authenticated.user.username,
      firstName: authenticated.user.global_name ?? authenticated.user.username,
      photoUrl: authenticated.user.avatar
        ? `https://cdn.discordapp.com/avatars/${authenticated.user.id}/${authenticated.user.avatar}.png`
        : undefined,
    };
  }
  await recordAuthOutcome(outcome, host, host);
  return outcome;
}

export function normalizeAccountLinkCode(value: string): string {
  return value.toUpperCase().replace(/[\s-]+/g, "").replace(/[IL]/g, "1").replace(/O/g, "0");
}

export async function createAccountLinkCode(): Promise<{ code: string; expiresAt: string }> {
  if (!supabase) throw new Error("Supabase is not configured");
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!accessToken || !supabaseUrl || !anonKey) throw new Error("Sign in first");
  const response = await fetch(`${supabaseUrl}/functions/v1/account-link-code`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: "{}",
  });
  const body = await response.json().catch(() => ({})) as {
    code?: string;
    expires_at?: string;
    error?: { message?: string };
  };
  if (!response.ok || !body.code || !body.expires_at) {
    throw new Error(body.error?.message ?? "Could not create a link code");
  }
  return { code: body.code, expiresAt: body.expires_at };
}

export async function shareInternationalGrowthLink(
  host: InternationalHost,
  input: { code: string; url: string; text: string },
): Promise<InternationalShareResult> {
  await initializeInternationalHost(host);
  if (host === "line") {
    const { default: liff } = await import("@line/liff");
    if (!liff.isApiAvailable("shareTargetPicker")) return "failed";
    let url = input.url;
    try {
      url = await liff.permanentLink.createUrlBy(input.url);
    } catch {
      // A URL outside the configured LIFF endpoint cannot become permanent;
      // use the canonical HTTPS fallback supplied by the server/client.
    }
    try {
      const result = await liff.shareTargetPicker([
        { type: "text", text: `${input.text}\n${url}` },
      ]);
      return result?.status === "success" ? "shared" : "cancelled";
    } catch {
      return "failed";
    }
  }
  if (host === "discord") {
    if (!discordSdk) return "failed";
    try {
      const result = await discordSdk.commands.shareLink({
        message: input.text,
        custom_id: `share_${input.code}`,
      });
      return result.success ? "shared" : "cancelled";
    } catch {
      return "failed";
    }
  }

  const { sharing } = await import("@microsoft/teams-js");
  try {
    if (!sharing.isSupported()) return "failed";
    await sharing.shareWebContent({
      content: [{ type: "URL", url: input.url, message: input.text, preview: true }],
    });
    return "shared";
  } catch (error) {
    return classifyTeamsShareError(error);
  }
}

/** Teams error 8000 is the documented user cancellation. Unsupported
 * environments (including 9000) and permission failures use web fallback. */
export function classifyTeamsShareError(error: unknown): InternationalShareResult {
  const code =
    typeof error === "object" && error !== null && "errorCode" in error
      ? Number((error as { errorCode: unknown }).errorCode)
      : null;
  if (code === 8000) return "cancelled";
  if (code === 9000) return "failed";
  return "failed";
}
